import { resolveLocaleForNumberFormat } from './numberFormat';
import type { SupportedLocale } from './numberFormat';

/**
 * Detects the decimal separator used by a given locale.
 * Uses Intl.NumberFormat.formatToParts to reliably extract the decimal separator.
 * Results are cached per locale to avoid recreating Intl.NumberFormat on every call.
 */
const decimalSeparatorCache = new Map<SupportedLocale, string>();

function getDecimalSeparator(locale: SupportedLocale): string {
  if (decimalSeparatorCache.has(locale)) {
    return decimalSeparatorCache.get(locale) as string;
  }
  const parts = new Intl.NumberFormat(resolveLocaleForNumberFormat(locale)).formatToParts(1.1);
  const separator = parts.find((p) => p.type === 'decimal')?.value ?? '.';
  decimalSeparatorCache.set(locale, separator);
  return separator;
}

/**
 * Attempts to parse a localized numeric string into a float.
 * Given an input string and a known decimal separator, strips the thousands separator
 * and normalizes the decimal separator to '.' before parsing.
 */
function parseWithDecimal(input: string, decimalSep: string): number {
  const thousandsSep = decimalSep === ',' ? '.' : ',';
  const decimalCount = input.split(decimalSep).length - 1;
  if (decimalCount > 1) return NaN;

  // Validate thousands separator grouping: every group after the first must be exactly 3 digits
  const [integerPart] = input.split(decimalSep);
  const thousandsGroups = integerPart.split(thousandsSep);

  if (thousandsGroups.length > 1) {
    const invalidGrouping = thousandsGroups.slice(1).some((group) => group.length !== 3);
    if (invalidGrouping) return NaN;
  }

  const normalized = input.split(thousandsSep).join('').replace(decimalSep, '.');
  return parseFloat(normalized);
}

/**
 * Parses a localized numeric string into a plain JS number.
 *
 * While typing, users may use either ',' or '.' as the decimal separator regardless
 * of their locale. This function resolves the ambiguity using the optional `min`/`max`
 * parameters: if the primary interpretation falls outside [min, max], the alternative
 * interpretation is tried.
 *
 * @param input - Raw string typed by the user (e.g. "7,5", "7.500", "1.250,75")
 * @param locale - The user's locale (determines which separator is canonical)
 * @param min - Optional lower bound used to disambiguate separators
 * @param max - Optional upper bound used to disambiguate separators
 * @returns Parsed number, or `null` if the string cannot be parsed
 *
 * @example
 * // es-MX uses '.' as decimal → "7,500" primary = 7500, but 7500 > 10 → retry as 7.5
 * parseLocalizedNumber("7,500", "es-MX", undefined, 10); // → 7.5
 *
 * @example
 * // es-CO uses ',' as decimal → "7,5" → 7.5
 * parseLocalizedNumber("7,5", "es-CO"); // → 7.5
 */
export function parseLocalizedNumber(
  input: string,
  locale: SupportedLocale,
  min?: number,
  max?: number
): number | null {
  if (!input || input.trim() === '') return null;

  const decimalSep = getDecimalSeparator(locale);
  const altDecimalSep = decimalSep === ',' ? '.' : ',';

  const inRange = (v: number): boolean =>
    (min === undefined || v >= min) && (max === undefined || v <= max);

  const primary = parseWithDecimal(input, decimalSep);

  if (!Number.isNaN(primary)) {
    if (inRange(primary)) {
      return primary;
    }
    // primary out of range: try alternative interpretation
    const alternative = parseWithDecimal(input, altDecimalSep);
    if (!Number.isNaN(alternative) && inRange(alternative)) {
      return alternative;
    }
    // both out of range or alternative is NaN: return primary anyway
    return primary;
  }

  // primary is NaN: try alternative separator
  const alternative = parseWithDecimal(input, altDecimalSep);
  return Number.isNaN(alternative) ? null : alternative;
}
